import React from "react";
import {Alert, Card, List, Space, Tag, Tooltip, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Link} from "react-router-dom";
import {useTranslate} from "../../../../hooks/translate.hook";
import {HealthCheckWithGroup} from "./dashboard.types";
import {formatHealthCenterDateTime} from "../../health-center.helpers";
import {DashboardScheduleValue} from "./dashboard.schedule-value";

const {Text} = Typography;

interface DashboardIssuesProps {
    checks: HealthCheckWithGroup[];
}

export const DashboardIssues: React.FC<DashboardIssuesProps> = ({checks}) => {
    const t = useTranslate();
    const hasIssues = checks.length > 0;

    return (
        <Card
            className={"health-center-dashboard__issues" + (hasIssues ? " health-center-dashboard__issues--failed" : " health-center-dashboard__issues--healthy")}
            title={
                <Space>
                    <FontAwesomeIcon
                        icon={["fas", hasIssues ? "exclamation-triangle" : "check-circle"]}
                    />
                    <span>
                        {t("HEALTH-CENTER.DASHBOARD.REQUIRES-ATTENTION", {
                            defaultValue: "Requires attention",
                        })}
                    </span>
                </Space>
            }
            extra={hasIssues ? <Tag color="error">{checks.length}</Tag> : null}
        >
            {!hasIssues ? (
                <Alert
                    type="success"
                    showIcon
                    message={t("HEALTH-CENTER.DASHBOARD.ALL-OPERATIONAL", {
                        defaultValue: "All checks are operational",
                    })}
                    description={t("HEALTH-CENTER.DASHBOARD.NO-ACTIVE-ISSUES", {
                        defaultValue: "There are no active health check issues.",
                    })}
                />
            ) : (
                <List
                    dataSource={checks}
                    renderItem={(check) => (
                        <List.Item className="health-center-dashboard__issue-row">
                            <div className="health-center-dashboard__issue-icon">
                                <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} />
                            </div>
                            <div className="health-center-dashboard__issue-content">
                                <Space size={[8, 6]} wrap>
                                    <Text strong className="health-center-dashboard__check-name">
                                        {check.checkerName}
                                    </Text>
                                    <Tag>{check.groupName}</Tag>
                                    {check.lastIssue ? <Tag color="error">{check.lastIssue.status}</Tag> : null}
                                </Space>
                                {check.lastIssue ? (
                                    <Text type="danger">{check.lastIssue.description}</Text>
                                ) : (
                                    <Text type="secondary">
                                        {t("HEALTH-CENTER.DASHBOARD.NO-ISSUE-DESCRIPTION", {
                                            defaultValue: "No issue details provided",
                                        })}
                                    </Text>
                                )}
                            </div>
                            <div className="health-center-dashboard__issue-schedule">
                                <DashboardScheduleValue
                                    label={t("HEALTH-CENTER.DASHBOARD.LAST-CHECKED-AT", {defaultValue: "Last checked"})}
                                    value={formatHealthCenterDateTime(check.lastCheckedAt)}
                                />
                                <DashboardScheduleValue
                                    highlighted
                                    label={t("HEALTH-CENTER.DASHBOARD.NEXT-CHECK-AT", {defaultValue: "Next check"})}
                                    value={formatHealthCenterDateTime(check.nextCheckAt)}
                                />
                                <DashboardScheduleValue
                                    label={t("HEALTH-CENTER.DASHBOARD.HOURS", {defaultValue: "Hours"})}
                                    value={t("HEALTH-CENTER.DASHBOARD.HOURS_VALUES", {
                                        hours: check.hours.join(", "),
                                        defaultValue: "{{hours}}",
                                    })}
                                />
                            </div>
                            <div className="health-center-dashboard__issue-action-cell">
                                {check.lastIssue ? (
                                    <Tooltip title={t("HEALTH-CENTER.DASHBOARD.OPEN-ISSUE", {defaultValue: "Open issue"})}>
                                        <Link
                                            className="health-center-dashboard__issue-action"
                                            to={`/health-center/issues/${check.lastIssue.id}`}
                                            aria-label={t("HEALTH-CENTER.DASHBOARD.OPEN-ISSUE", {defaultValue: "Open issue"})}
                                        >
                                            <FontAwesomeIcon icon={["fas", "eye"]} />
                                        </Link>
                                    </Tooltip>
                                ) : null}
                            </div>
                        </List.Item>
                    )}
                />
            )}
        </Card>
    );
};
